import React, { useState, useEffect, useCallback, useRef } from 'react';
import './UsageDashboard.css';
import { IoClose } from 'react-icons/io5';
import { usageService } from '../services/UsageService';
import { useStore } from '../store/useStore';
import { UsageSummary, UsageAlert } from '../types/usage';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';

interface UsageDashboardProps {
  onClose?: () => void;
}

const UsageDashboard: React.FC<UsageDashboardProps> = ({ onClose }) => {
  const { language, usagePreferences, user } = useStore();
  const topUpValue = useStore(state => state.topUpValue); // Select topUpValue directly
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [alerts, setAlerts] = useState<UsageAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRetry, setIsLoadingRetry] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const loadingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [displayMode, setDisplayMode] = useState<'total' | 'monthly'>(
    usagePreferences.display_mode
  );
  const [historicalDataCache, setHistoricalDataCache] = useState<Record<string, any>>({});
  const [lastRefresh, setLastRefresh] = useState<number>(Date.now());
  const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

  const loadData = useCallback(async (isRetry = false) => {
    if (!user || loadingRef.current || !isInitialized) {
      console.log('Skipping load:', { 
        noUser: !user, 
        isLoading: loadingRef.current,
        isInitialized
      });
      return;
    }
    
    try {
      loadingRef.current = true;
      isRetry ? setIsLoadingRetry(true) : setIsLoading(true);
      setError(null);
      
      console.log('Loading usage data...', { isRetry });
      
      // Force a refresh of the usage data
      await usageService.refreshUsageData(user.uid, topUpValue);
      
      // Get the latest data with timeout
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      const dataPromise = Promise.all([
        usageService.getUsageSummary(user.uid, topUpValue, language),
        usageService.checkAlerts(user.uid, language)
      ]);
      
      const [currentSummary, currentAlerts] = await Promise.race([
        dataPromise,
        timeoutPromise
      ]) as [UsageSummary, UsageAlert[]];

      console.log('Dashboard loaded data:', {
        summary: currentSummary,
        alerts: currentAlerts
      });

      setSummary(currentSummary);
      setAlerts(currentAlerts);
    } catch (error) {
      console.error('Error loading usage data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load usage data';
      console.error('Detailed error:', errorMessage);
      
      // Provide more specific error messages to users
      const isTimeout = errorMessage.includes('timeout');
      const userMessage = language === 'es' 
        ? `Error al cargar los datos de uso${isRetry ? ' después de reintentar' : ''}${isTimeout ? '. Tiempo de espera agotado' : ''}. Por favor, inténtalo de nuevo en unos momentos.`
        : `Error loading usage data${isRetry ? ' after retry' : ''}${isTimeout ? '. Request timed out' : ''}. Please try again in a few moments.`;
      
      setError(userMessage);
      setSummary(null);
      setAlerts([]);
      
      // Retry loading after a delay if it's a network error or timeout and not already retrying
      if (!isRetry && (errorMessage.toLowerCase().includes('network') || isTimeout)) {
        console.log('Scheduling retry after error...');
        setTimeout(() => {
          console.log('Retrying data load...');
          loadData(true);
        }, 5000); // Retry after 5 seconds
      }
    } finally {
      loadingRef.current = false;
      isRetry ? setIsLoadingRetry(false) : setIsLoading(false);
    }
    console.log('UsageDashboard: loadData useEffect triggered, topUpValue:', topUpValue);
  }, [user, language, topUpValue]); // Add topUpValue to dependency array

  // Initialize usage service when user changes
  useEffect(() => {
    const initializeService = async () => {
      if (!user) {
        setIsInitialized(false);
        setSummary(null);
        return;
      }

      if (isInitialized) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        setSummary(null);
        setAlerts([]);
        
        await usageService.initialize(user.uid, topUpValue);
        console.log('Usage service initialized');
        setIsInitialized(true);
        
        // Load initial data
        const [currentSummary, currentAlerts] = await Promise.all([
          usageService.getUsageSummary(user.uid, topUpValue, language),
          usageService.checkAlerts(user.uid, language)
        ]);
        
        setSummary(currentSummary);
        setAlerts(currentAlerts);
      } catch (error) {
        console.error('Error initializing usage service:', error);
        setError(language === 'es'
          ? 'Error al inicializar el servicio de uso'
          : 'Error initializing usage service');
        setIsInitialized(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeService();

    return () => {
      setIsInitialized(false);
      loadingRef.current = false;
    };
  }, [user?.uid, topUpValue, language]);

  // Refresh data periodically when initialized
  useEffect(() => {
    if (!isInitialized || !user || !summary) return;

    const loadWithCache = async () => {
      if (Date.now() - lastRefresh > REFRESH_INTERVAL && !loadingRef.current) {
        try {
          const [currentSummary, currentAlerts] = await Promise.all([
            usageService.getUsageSummary(user.uid, topUpValue, language),
            usageService.checkAlerts(user.uid, language)
          ]);
          
          setSummary(currentSummary);
          setAlerts(currentAlerts);
          setLastRefresh(Date.now());
        } catch (error) {
          console.error('Error refreshing usage data:', error);
        }
      }
    };

    const interval = setInterval(loadWithCache, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [isInitialized, user, summary, language, topUpValue, lastRefresh]);

  const handleDisplayModeChange = useCallback(async (mode: 'total' | 'monthly') => {
    setDisplayMode(mode);
    if (!user) return;

    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        'usagePreferences.display_mode': mode,
        lastUpdated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating display mode preference:', error);
    }
  }, [user]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(language === 'es' ? 'es-ES' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat(language === 'es' ? 'es-ES' : 'en-US').format(num);
  };

  const formatMonthYear = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return new Intl.DateTimeFormat(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long'
    }).format(date);
  };

  if (!isInitialized || !summary) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex flex-col items-center justify-center space-y-4">
          {isLoading && (
            <div className="text-lg text-gray-600 dark:text-gray-300">
              {language === 'es' ? 'Cargando datos de uso...' : 'Loading usage data...'}
            </div>
          )}
          
          {error && (
            <div className="text-center">
              <div className="text-red-500 dark:text-red-400 font-medium mb-2">
                {language === 'es' ? 'Error al cargar datos' : 'Error loading data'}
              </div>
              <div className="text-sm text-red-400 dark:text-red-300 mb-4">{error}</div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  loadData(false);
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                {language === 'es' ? 'Reintentar' : 'Retry'}
              </button>
            </div>
          )}
          
          {!isLoading && !error && !summary && (
            <div className="text-lg text-gray-600 dark:text-gray-300">
              {language === 'es' ? 'No hay datos de uso disponibles' : 'No usage data available'}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 space-y-6 relative usage-dashboard overflow-y-auto max-h-[80vh] max-w-xl mx-auto border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex flex-col">
        {/* Title */}
        <h2 className="text-2xl font-bold dark:text-white mb-6">
          {language === 'es' ? 'Panel de Consumo' : 'Usage Dashboard'}
        </h2>

        {/* Controls */}
        <div className="flex flex-wrap gap-4 items-center">
          {/* Stripe Button */}
          <div className="flex-grow sm:flex-grow-0">
            <stripe-buy-button
              buy-button-id={
                language === 'en'
                  ? "buy_btn_1QqHPHKXRQI7jo0jWKmGdTjH"
                  : "buy_btn_1QrV5aKXRQI7jo0jsh8JSe0j"
              }
              publishable-key="pk_live_51Qpq2dKXRQI7jo0jYOURN9UX36AOs5kznDqHTAiBUaYmTQur3WvHEcCHGM5qFLveWGBgM6LguRAjGJKDzVauGnFe00CbOg1MaL"
            />
          </div>

          {/* Display Mode Controls */}
          <div className="flex gap-2">
            <button
              onClick={() => handleDisplayModeChange('monthly')}
              className={`px-4 py-2 rounded-lg ${
                displayMode === 'monthly'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {language === 'es' ? 'Mensual' : 'Monthly'}
            </button>
            <button
              onClick={() => handleDisplayModeChange('total')}
              className={`px-4 py-2 rounded-lg ${
                displayMode === 'total'
                  ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-full'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full'
              }`}
            >
              {language === 'es' ? 'Total' : 'Total'}
            </button>
          </div>
        </div>

        {/* Close button - absolute position */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-3 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-all duration-200 cursor-pointer"
          aria-label="Close dashboard"
        >
          <IoClose size={28} className="m-1" />
        </button>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, index) => (
            <div
              key={index}
              className="p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800"
            >
              <p className="font-medium">{alert.message}</p>
              <p className="text-sm mt-1">
                {language === 'es' ? 'Actual: ' : 'Current: '}
                {alert.type === 'cost_limit'
                  ? formatCurrency(alert.current_value)
                  : formatNumber(alert.current_value)}
                {' / '}
                {alert.type === 'cost_limit'
                  ? formatCurrency(alert.limit)
                  : formatNumber(alert.limit)}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Current Month Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-blue-800 dark:text-blue-100">
            {language === 'es' ? 'Tokens Totales' : 'Total Tokens'}
          </h3>
          <p className="text-2xl font-bold text-blue-900 dark:text-blue-50">
            {formatNumber(summary.current_month.total_tokens)}
          </p>
          <div className="text-sm text-blue-700 dark:text-blue-200 mt-1">
            <p>
              {language === 'es' ? 'Restantes: ' : 'Remaining: '}
              {formatNumber(summary.limits?.token_limit_remaining ?? 0)}
            </p>
            <p className="text-xs opacity-75">
              {summary.limits?.token_limit_note}
            </p>
          </div>
        </div>
        <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-green-800 dark:text-green-100">
            {language === 'es' ? 'Coste Total' : 'Total Cost'}
          </h3>
          <p className="text-2xl font-bold text-green-900 dark:text-green-50">
            {formatCurrency(summary.current_month.total_cost)}
          </p>
          <p className="text-sm text-green-700 dark:text-green-200 mt-1">
            {language === 'es' ? 'Restante: ' : 'Remaining: '}
            {formatCurrency(summary.limits?.cost_limit_remaining ?? 0)}
          </p>
        </div>
      </div>

      {/* Model Breakdown */}
      <div className="mt-6">
        <h3 className="text-lg font-medium mb-4 dark:text-white">
          {language === 'es' ? 'Desglose por Modelo' : 'Model Breakdown'}
        </h3>
        <div className="space-y-4">
          {Object.entries(summary.current_month.breakdown_by_model).map(([modelId, data]) => (
            <div
              key={modelId}
              className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg"
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium dark:text-white">{modelId}</h4>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {formatNumber(data.tokens.total_tokens)}
                  </span>
                </div>
                <span className="font-medium dark:text-white">
                  {formatCurrency(data.cost.total_cost)}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
                <p>
                  <span>{language === 'es' ? 'Prompt: ' : 'Prompt: '}</span>
                  <span className="font-medium">{formatNumber(data.tokens.prompt_tokens)}</span>
                </p>
                <p>
                  <span>{language === 'es' ? 'Completion: ' : 'Completion: '}</span>
                  <span className="font-medium">{formatNumber(data.tokens.completion_tokens)}</span>
                </p>
                <p>
                  <span>{language === 'es' ? 'P. Cost: ' : 'P. Cost: '}</span>
                  <span className="font-medium">{formatCurrency(data.cost.prompt_cost)}</span>
                </p>
                <p>
                  <span>{language === 'es' ? 'C. Cost: ' : 'C. Cost: '}</span>
                  <span className="font-medium">{formatCurrency(data.cost.completion_cost)}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Historical Data */}
      {displayMode === 'total' && (
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-4 dark:text-white">
            {language === 'es' ? 'Historial Mensual' : 'Monthly History'}
          </h3>
          <div className="space-y-4">
            {summary.historical.monthly_totals.map((monthData) => (
              <div
                key={monthData.month}
                className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium dark:text-white">
                      {formatMonthYear(monthData.month)}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {formatNumber(monthData.tokens.total_tokens)} {language === 'es' ? 'tokens' : 'tokens'}
                    </p>
                  </div>
                  <p className="font-medium dark:text-white">
                    {formatCurrency(monthData.cost.total_cost)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageDashboard;